import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Send } from "lucide-react";
import { API } from "../../App";
import { Button } from "../ui/button";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "–");
const fmtDay = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" }) : "the gate date (not set)");

// Admin: the one-off "gate is open, apply now" email, sent by the daily cron on the gate date at 08:00 Madrid.
export default function SojournsGateMail({ headers, adminEmail }) {
  const [state, setState] = useState(null);
  const [testTo, setTestTo] = useState(adminEmail || "");
  const [busy, setBusy] = useState("");

  const load = () => axios.get(`${API}/admin/sojourns/gate-mail`, { headers }).then((r) => setState(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const post = async (body, label) => {
    setBusy(label);
    try {
      const r = await axios.post(`${API}/admin/sojourns/gate-mail/send`, body, { headers });
      toast.success(body.test_to ? `Test sent to ${body.test_to}` : `Sending to ${r.data.recipients} people`);
      if (!body.test_to) setTimeout(load, 4000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send");
    } finally {
      setBusy("");
    }
  };

  const sendNow = () => {
    if (!state) return;
    const msg = state.sent
      ? `It already went out on ${fmt(state.sent.sent_at)}. Send it again to anyone who hasn't had it?`
      : `Send "The gate is open" to ${state.recipients} people now${state.gate_open ? "" : ", before the gate date"}?`;
    if (window.confirm(msg)) post({ force: true }, "send");
  };

  return (
    <div className="bg-white rounded-2xl border border-stone-200 p-5 mb-6" data-testid="admin-sojourns-gate-mail">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="font-medium text-[#2E2A26]">Gate-open email</h2>
          <p className="text-xs text-[#3D4035]/60 mt-1 max-w-xl" data-testid="admin-gate-schedule">
            Goes to everyone on the list once, at 08:00 (Madrid) on {fmtDay(state?.reveal_date)}: "The gate is open", how many seats are already held, and an Apply now button. Follows the gate date above if you change it.
          </p>
        </div>
        <Button onClick={sendNow} disabled={!state || busy === "send"} variant="outline" className="rounded-full" data-testid="admin-gate-send-now">
          <Send className="w-4 h-4 mr-2" /> {busy === "send" ? "Sending" : state?.sent ? "Send again" : "Send now"}
        </Button>
      </div>

      <p className="text-xs mt-3" data-testid="admin-gate-status">
        {state?.sent
          ? <span className="text-emerald-700">Sent {fmt(state.sent.sent_at)} to {state.sent.sent} people{state.sent.failed ? ` (${state.sent.failed} failed)` : ""}.</span>
          : <span className="text-[#3D4035]/50">Not sent yet · {state?.recipients ?? 0} on the list.</span>}
      </p>

      <div className="flex flex-wrap items-end gap-2 mt-4">
        <div className="flex-1 min-w-[220px]">
          <label htmlFor="gate-test-to" className="block text-xs text-[#3D4035]/60 mb-1">Send me a test</label>
          <input id="gate-test-to" type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} className="h-10 w-full px-3 rounded-lg border border-stone-200 text-sm bg-white" data-testid="admin-gate-test-email" />
        </div>
        <Button onClick={() => (testTo.trim() ? post({ test_to: testTo.trim() }, "test") : toast.error("Add an email to send the test to"))} disabled={busy === "test"} className="bg-[#C85A38] hover:bg-[#A84830] text-white rounded-full h-10" data-testid="admin-gate-send-test">
          <Mail className="w-4 h-4 mr-2" /> {busy === "test" ? "Sending" : "Send test"}
        </Button>
      </div>
    </div>
  );
}
